/**
 * ConfirmWithReason — one confirmation modal for audited administrative actions.
 *
 * Every audit-sensitive admin action (role/permission save, user unlock/deactivate, company
 * settings, master deletes, integration dispatch/replay) goes through this modal so the action
 * always carries a mandatory reason and, when `requireEsign` is set, a 21 CFR Part 11 e-signature
 * (username + password verified through `ESignAuth`).
 *
 * Two ways to use it:
 *
 *   // 1. Hook (preferred — one line per action)
 *   const { confirm, modal } = useConfirmWithReason();
 *   const r = await confirm({ title: 'Delete equipment', description: 'MWH054 will be removed.', danger: true });
 *   if (!r) return;                 // cancelled
 *   api.delete({ id, reason: r.reason });
 *   ...
 *   return (<>{modal}{...page}</>);
 *
 *   // 2. Controlled component
 *   <ConfirmWithReason open={open} title="Save permissions" onConfirm={({ reason }) => ...} onCancel={...} />
 *
 * The resolved value is `{ reason, esign }` where `esign` is `{ username, comment }` when an
 * e-signature was captured, otherwise `null`.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Input, Typography, Alert } from 'antd';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import ESignAuth from '@/components/ESignAuth';
import PageModal from '@/components/PageModal';
import useAppSetting from '@/hooks/useAppSetting';
import { ESIGN_REQUIRED_SETTING } from '@/utils/esignSetting';

const { Text } = Typography;

export const MIN_REASON_LENGTH = 5;

export default function ConfirmWithReason({
  open,
  title = 'Confirm',
  description = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = false,
  requireEsign = false,
  esignReq = undefined,
  reasonLabel = 'Reason for this change',
  reasonPlaceholder = 'Why is this change being made? It is recorded in the audit trail.',
  loading = false,
  onConfirm,
  onCancel,
}) {
  const esignRequiredGlobally = useAppSetting(ESIGN_REQUIRED_SETTING, true);
  const willSign = requireEsign && esignRequiredGlobally;
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);

  const trimmed = reason.trim();
  const reasonInvalid = trimmed.length < MIN_REASON_LENGTH;

  const reset = () => {
    setReason('');
    setTouched(false);
    setEsignOpen(false);
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const finish = (esign) => {
    const payload = { reason: trimmed, esign: esign || null };
    reset();
    onConfirm?.(payload);
  };

  const handleOk = () => {
    setTouched(true);
    if (reasonInvalid) return;
    if (requireEsign) {
      setEsignOpen(true);
      return;
    }
    finish(null);
  };

  return (
    <>
      <PageModal
        open={open}
        icon={danger ? <AlertTriangle size={22} color="#ef4444" /> : <ShieldCheck size={22} />}
        title={title}
        description={description}
        onCancel={handleCancel}
        width={520}
        destroyOnHidden
        maskClosable={false}
        footer={(
          <div className="flex justify-end gap-2">
            <Button onClick={handleCancel}>{cancelText}</Button>
            <Button type="primary" danger={danger} disabled={reasonInvalid && touched} loading={loading} onClick={handleOk}>
              {willSign ? `${confirmText} & sign` : confirmText}
            </Button>
          </div>
        )}
      >
        <Text strong>{reasonLabel}</Text>
        <Input.TextArea
          autoFocus
          rows={3}
          value={reason}
          maxLength={500}
          showCount
          onChange={(e) => setReason(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder={reasonPlaceholder}
          status={touched && reasonInvalid ? 'error' : undefined}
          data-testid="confirm-reason"
          style={{ marginTop: 6 }}
        />
        {touched && reasonInvalid && (
          <Text type="danger" style={{ fontSize: 12 }}>
            A reason of at least {MIN_REASON_LENGTH} characters is required — this is an audited change.
          </Text>
        )}
        {willSign && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Electronic signature required"
            description="You will be asked to re-enter your username and password (21 CFR Part 11)."
          />
        )}
      </PageModal>
      {requireEsign && (
        <ESignAuth
          visible={esignOpen}
          req={{ ...(esignReq || {}), reason: trimmed }}
          onCancel={() => setEsignOpen(false)}
          onSubmit={(values) => {
            setEsignOpen(false);
            finish({ username: values.username, comment: values.comment });
          }}
        />
      )}
    </>
  );
}

/**
 * Imperative helper: `const { confirm, modal } = useConfirmWithReason();`
 * `confirm(options)` resolves with `{ reason, esign }` or `null` when cancelled.
 * Render `{modal}` once anywhere in the page tree.
 */
export function useConfirmWithReason() {
  const [state, setState] = useState({ open: false, options: {} });
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const settle = useCallback((value) => {
    setState({ open: false, options: {} });
    const r = resolver.current;
    resolver.current = null;
    r?.(value);
  }, []);

  const modal = useMemo(
    () => (
      <ConfirmWithReason
        open={state.open}
        {...state.options}
        onConfirm={(v) => settle(v)}
        onCancel={() => settle(null)}
      />
    ),
    [state, settle]
  );

  return { confirm, modal };
}
